import React from 'react'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardVoiceOutlinedIcon from '@mui/icons-material/KeyboardVoiceOutlined'
import MicOffOutlinedIcon from '@mui/icons-material/MicOffOutlined'
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import { Box, CircularProgress, Dialog, DialogActions, Grid, Tooltip } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Skeleton from '@mui/material/Skeleton'
import AgoraRTC from 'agora-rtc-sdk-ng'
import debounce from 'lodash/debounce'
import Notes from '@mui/icons-material/Notes'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useCallback, useEffect, useState } from 'react'

// Load the plugins
dayjs.extend(utc)
dayjs.extend(timezone)
import { Spinner } from 'react-bootstrap'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { toast } from 'react-toastify'
import blockedAccessImage from 'src/assets/blocked_permissions.png'
import allowPermissionOnTopLeftImage from 'src/assets/click_allow_at_top_left.svg'
import firstTimePermissionImage from 'src/assets/first_time_permission.svg'
import googleCalendarIcon from 'src/assets/google_calendar_icon.svg'
import { sortArrayMomentBased } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsHelper'
import {
  currency_list,
  EXTERNAL_FEATURE_TYPES,
  REDUX_STATUS_IDLE,
  REDUX_STATUS_LOADING,
} from 'src/consumer/constants'
import AddOrViewInvestigationsForSelectedPerson from 'src/mentor/components/Health/investigations/AddOrViewInvestigations'
import PrintableScreeningReportComponent from 'src/mentor/components/ScreeningReportMakingPage/printableReport/PrintableScreeningReportComponent'
import TherapyReportPreview from 'src/mentor/components/TherapyReportMakingPage/components/TherapyReportPreview'
import { isCommonResponseSuccessful } from '../../../mentor/helpers/utilityFunctions'
import { getAllDomains } from '../../../mentor/layout/utils'
import {
  getMPDataWithoutParams,
  getMPDataWithParams,
  postMPDataWithParams,
} from '../../../mentor/services'
import { addToGoogleCalendarLink, checkAndGetTargetValue } from '../../helpers/utilFunctions'
import { mediaURL } from '../../services'
import { handleAccept } from '../ActivityPlansPage/activityPlanHelpers'
import { ViewOnlyPrescription, ViewOnlyPrescriptionVarTwo } from '../MyLiveSessions'
import {
  LiveSessionErrorSign,
  LiveSessionPermissionsButtonVarOne,
  LiveSessionPermissionsImageVarOne,
  LiveSessionPermissionsParentVarOne,
  LiveSessionPermissionsTitleVarOne,
} from '../ServiceProvidersListingPage/ServiceProvidersListingComponentsStyles'
import {
  CustomInput,
  CustomSelect,
  ImageViewComponent,
  OverflowingArrayChips,
  selectTypes,
} from '../helperComponents'
import {
  AddtoCalendarStyledButton,
  AssignButton,
  EllipsisText,
  LSActivityPlanCells,
  LSActivityPlanDescriptionTooltipText,
  LSActivityPlanFilterContainer,
  LSActivityPlanItemsContainer,
  LSActivityPlanTableContainer,
  LSActivityPlanTableRows,
  LSOfflineSidebarContainer,
  LSPastReportsSkeleton,
  LSPlanBeingSentModalContent,
  LSPlanBeingSentModalDescription,
  LSPlanBeingSentModalDialogTitle,
  LSPlanBeingSentModalTitle,
  LSPlanBeingSentModalTotalDays,
  LSPlanOfferAcceptButton,
  LSPlanOfferCancelButton,
  MicCameraToggleContainer,
  StartingCircularProgress,
} from './LiveSessionPageStyles'
import ConnectingLines from './components/ConnectingLines'
import { getNextCompletedBookedSlots } from 'src/clinic/components/ClinicCounterScreen/utils'
import { getPastPatientReports } from 'src/reducers/emr/services/pastPatientReports'
import { useDispatch, useSelector } from 'react-redux'
import { selectEmrPastReports } from 'src/reducers/emr/emrReducer'
import {
  getSelectedCameraInLocalStorage,
  getSelectedMicrophoneInLocalStorage,
  getSelectedSpeakerInLocalStorage,
} from 'src/consumer/services/localstorageServices'
import RenderNoteOverReport from 'src/consumer/components/LiveSessionPage/components/RenderNoteOverReport/RenderNoteOverReport'
import VisitReportsComponent from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/VisitReportsComponent'

// helpers :

export function getMmssString(n, hideBrackets = false) {
  let minutes = Math.floor(n / 60)
  let seconds = n % 60
  let minutesString = minutes?.toString().padStart(2, '0')
  let secondsString = seconds?.toString().padStart(2, '0')
  return `${minutesString}:${secondsString}`
  // return hideBrackets
  //   ? `Time Spent: ${minutesString}:${secondsString}`
  //   : `(Time Spent: ${minutesString}:${secondsString})`
}

export function getHoursMinutesAndSecondsFromSecondsString(seconds) {
  let minutes = Math.floor(seconds / 60)
  let hours = Math.floor(minutes / 60)
  let remainingMinutes = minutes % 60
  let remainingSeconds = seconds % 60
  let finalString = ''
  if (hours > 0) {
    finalString += `${hours}h `
  }
  if (remainingMinutes > 0) {
    finalString += `${remainingMinutes}m `
  }
  if (remainingSeconds > 0) {
    finalString += `${remainingSeconds}s`
  }
  return finalString
}

export function sortEventsInOrder(inputArray, fieldToCompare, ascending = true) {
  if (Array.isArray(inputArray)) {
    const compareDates = (a, b) => {
      const dateA = new Date(a?.[fieldToCompare])
      const dateB = new Date(b?.[fieldToCompare])
      if (ascending) {
        return dateA - dateB
      } else {
        return dateB - dateA
      }
    }

    return inputArray.sort(compareDates)
  } else {
    return []
  }
}

export function getActiveAndUpcomingEvents(inputArray, number = 3, currentBookedEvent) {
  if (Array.isArray(inputArray)) {
    const startOfDay = dayjs.utc(currentBookedEvent?.startTime).local().startOf('day')
    const upcomingBookedEvents = inputArray?.filter(
      (item) => !item?.markedCompleted && currentBookedEvent?.id !== item?.id,
    )

    return upcomingBookedEvents?.slice(0, number)
  } else {
    return []
  }
}

export async function getAndSetMicrophones({ setAvailableMicrophones, setSelectedMicrophone }) {
  try {
    const microphones = await AgoraRTC.getMicrophones()
    setAvailableMicrophones(microphones || [])
    
    // Handle empty array case (common on iOS Safari after permission grant)
    if (!microphones || microphones.length === 0) {
      console.warn('No microphones found during device enumeration')
      return
    }
    
    const defaultMic = microphones[0].deviceId
    const localStorageMic = getSelectedMicrophoneInLocalStorage()

    const isLocalMicAvailable = microphones.find((item) => item.deviceId === localStorageMic)

    if (localStorageMic && isLocalMicAvailable) {
      setSelectedMicrophone(localStorageMic)
    } else {
      setSelectedMicrophone(defaultMic)
      localStorage.setItem('selectedMicrophone', defaultMic)
    }
  } catch (error) {
    console.warn('Error getting microphones:', error)
    // Update state to empty array on error to ensure consistent state
    setAvailableMicrophones([])
    // Re-throw to allow callers with retry logic to handle the error
    throw error
  }
}

export async function getAndSetCameras({ setAvailableCameras, setSelectedCamera }) {
  try {
    const cameras = await AgoraRTC.getCameras()
    setAvailableCameras(cameras || [])
    
    // Handle empty array case (common on iOS Safari after permission grant)
    if (!cameras || cameras.length === 0) {
      console.warn('No cameras found during device enumeration')
      return
    }
    
    const defaultCam = cameras[0].deviceId
    const localStorageCam = getSelectedCameraInLocalStorage()
    const isLocalCamAvailable = cameras.find((item) => item.deviceId === localStorageCam)

    if (localStorageCam && isLocalCamAvailable) {
      setSelectedCamera(localStorageCam)
    } else {
      setSelectedCamera(defaultCam)
      localStorage.setItem('selectedCamera', defaultCam)
    }
  } catch (error) {
    console.warn('Error getting cameras:', error)
    // Update state to empty array on error to ensure consistent state
    setAvailableCameras([])
    // Re-throw to allow callers with retry logic to handle the error
    throw error
  }
}

export async function getAndSetSpeakers({ setAvailableSpeakers, setSelectedSpeaker }) {
  try {
    const speakers = await AgoraRTC.getPlaybackDevices()
    setAvailableSpeakers(speakers || [])
    
    // Handle empty array case (common on iOS Safari after permission grant)
    if (!speakers || speakers.length === 0) {
      console.warn('No speakers found during device enumeration')
      return
    }
    
    const defaultSpeaker = speakers[0].deviceId
    const localStorageSpeaker = getSelectedSpeakerInLocalStorage()
    const isLocalSpeakerAvailable = speakers.find((item) => item.deviceId === localStorageSpeaker)

    if (localStorageSpeaker && isLocalSpeakerAvailable) {
      setSelectedSpeaker(localStorageSpeaker)
    } else {
      setSelectedSpeaker(defaultSpeaker)
      localStorage.setItem('selectedSpeaker', defaultSpeaker)
    }
  } catch (error) {
    console.warn('Error getting speakers:', error)
    // Update state to empty array on error to ensure consistent state
    setAvailableSpeakers([])
    // Re-throw to allow callers with retry logic to handle the error
    throw error
  }
}

// components

export default function LiveSessionSidebar({
  sidebarSelectMenu,
  sidebarSelectedMenuItem,
  setSidebarSelectedMenuItem,
}) {
  return (
    <LSOfflineSidebarContainer>
      {sidebarSelectMenu?.map((item, index) => (
        <div
          style={{
            textAlign: 'center',
            cursor: 'pointer',
            fontWeight: item?.id === sidebarSelectedMenuItem ? '600' : '400',
            color: item?.id === sidebarSelectedMenuItem ? '#FFF' : '#EFEFEF',
          }}
          key={index}
          onClick={() => {
            setSidebarSelectedMenuItem(item?.id)
          }}
        >
          {item?.label}
        </div>
      ))}
    </LSOfflineSidebarContainer>
  )
}


export function ActivityPlansListing({ mentorId, clientName, clientId }) {
  const [customPlans, setCustomPlans] = useState([])
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loadingPlans, setLoadingMorePlans] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [filters, setFilters] = useState({
    domainIds: [],
  })
  const [planToSend, setPlanToSend] = useState()
  const [showPlanSendConfirmation, setShowPlanSendConfirmation] = useState(false)
  const [sending, setSending] = useState(false)

  const [allDomains, setAllDomains] = useState([])

  async function getMoreActivityPlans(page, search, funcFilters = filters) {
    const response = await getMPDataWithParams('/api/secure/teacher/activity-plans/summary', {
      page: page,
      size: 10,
      search,
      mentorId: mentorId,
      domainIds: funcFilters?.domainIds?.length > 0 ? funcFilters?.domainIds?.join(',') : null,
    })
    if (response.status === 200) {
      if (response.data && response.data.length > 0) {
        setCustomPlans((prev) => [...(prev || []), ...(response.data || [])])
        setPage((prev) => prev + 1)
      } else {
        setHasMore(false)
      }
    }
  }

  const debouncedSearch = useCallback(
    debounce(async (value) => {
      await getMoreActivityPlans(1, value)
      setLoadingMorePlans(false)
    }, 500),
    [],
  )

  async function getNextPage() {
    setLoadingMorePlans(true)
    await getMoreActivityPlans(page)
    setLoadingMorePlans(false)
  }

  async function searchFunction(e) {
    setLoadingMorePlans(true)
    setPage(1)
    setCustomPlans([])
    setSearch(e.target.value)
    debouncedSearch(e.target.value)
  }

  const [sentryRef] = useInfiniteScroll({
    loading: loadingPlans,
    hasNextPage: hasMore,
    onLoadMore: getNextPage,
    rootMargin: '0px 0px 400px 0px',
  })

  async function getDomains() {
    const response = await getAllDomains()
    if (response.status === 200) {
      if (response.data && response.data.length > 0) {
        setAllDomains(response.data)
      }
    }
  }

  async function changeFilters() {
    setLoadingMorePlans(true)
    setPage(1)
    setCustomPlans([])
    setHasMore(true)
    await getMoreActivityPlans(1, search, filters)
    setLoadingMorePlans(false)
  }

  async function Setup() {
    await Promise.all([getDomains()])
  }

  function handleChangeFilter(e) {
    setFilters((prev) => ({ ...prev, [e.target.id]: checkAndGetTargetValue(e) }))
  }

  function handleCloseAssignPlanDialog() {
    setShowPlanSendConfirmation(false)
  }

  function assignPlanToUser(planItem) {
    setPlanToSend(planItem)
    setShowPlanSendConfirmation(true)
  }

  async function suggestPlanToUser() {
    setSending(true)
    const resp = await postMPDataWithParams(
      '/api/secure/teacher/activity-plans/send-to-user',
      {},
      {
        activityPlanId: planToSend?.id,
        userId: clientId,
      },
    )
    if (resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)) {
      toast.success('Plan sent to the user.')
      handleCloseAssignPlanDialog()
    } else {
      toast.error('Something went wrong.')
    }
    setSending(false)
  }

  useEffect(() => {
    changeFilters()
  }, [filters])

  useEffect(() => {
    Setup()
  }, [])

  return (
    <div
      style={{
        padding: '20px 0px',
      }}
    >
      <LSActivityPlanFilterContainer container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomInput value={search} placeholder="Search..." onChange={searchFunction} />
        </Grid>
        <Grid item xs={12} md={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <CustomSelect
                isMulti
                id="domainIds"
                value={filters.domainIds.sort((a, b) => {
                  const titleA = a.title.toLowerCase() // Convert titles to lowercase for case-insensitive sorting
                  const titleB = b.title.toLowerCase()

                  if (titleA < titleB) {
                    return -1 // a should come before b
                  } else if (titleA > titleB) {
                    return 1 // a should come after b
                  } else {
                    return 0 // titles are equal
                  }
                })}
                placeholder="Select a domain..."
                onChangeEvent={handleChangeFilter}
                selectType={selectTypes.string}
                options={allDomains}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
              />
            </Grid>
          </Grid>
        </Grid>
      </LSActivityPlanFilterContainer>
      <LSActivityPlanTableContainer>
        <LSActivityPlanTableRows header>
          <LSActivityPlanCells></LSActivityPlanCells>
          <LSActivityPlanCells>Plan Title</LSActivityPlanCells>
          <LSActivityPlanCells>Plan Description</LSActivityPlanCells>
          <LSActivityPlanCells>Total Days</LSActivityPlanCells>
          <LSActivityPlanCells>Domains</LSActivityPlanCells>
          <LSActivityPlanCells></LSActivityPlanCells>
        </LSActivityPlanTableRows>
        <LSActivityPlanItemsContainer>
          {customPlans?.map((plan, idx) => (
            <LSActivityPlanTableRows key={plan.id} index={idx}>
              <LSActivityPlanCells index={idx}>
                <ImageViewComponent
                  imageId={plan.coverIcon}
                  src={`${mediaURL}${plan.coverIcon}`}
                  alt="cover_image"
                  variant="rounded"
                  sx={{
                    width: '50px',
                    height: '50px',
                  }}
                />
              </LSActivityPlanCells>
              <LSActivityPlanCells index={idx}>{plan.title}</LSActivityPlanCells>
              <LSActivityPlanCells index={idx}>
                <Tooltip
                  title={
                    <>
                      <LSActivityPlanDescriptionTooltipText>
                        {plan.description}
                      </LSActivityPlanDescriptionTooltipText>
                    </>
                  }
                >
                  <EllipsisText>{plan.description}</EllipsisText>
                </Tooltip>
              </LSActivityPlanCells>
              <LSActivityPlanCells index={idx}>{plan.totalDays}</LSActivityPlanCells>
              <LSActivityPlanCells index={idx}>
                <OverflowingArrayChips
                  allItems={plan.domains?.map((tag) => {
                    const foundTag = allDomains?.find((rTag) => rTag?.id === tag)
                    return !!foundTag ? foundTag : { id: tag, title: tag }
                  })}
                  getTitle={(item) => item?.title}
                  maxChips={2}
                  key={idx}
                />
              </LSActivityPlanCells>
              <LSActivityPlanCells actionButton index={idx}>
                <AssignButton onClick={() => assignPlanToUser(plan)}>Suggest</AssignButton>
              </LSActivityPlanCells>
            </LSActivityPlanTableRows>
          ))}
          {(loadingPlans || hasMore) && (
            <div ref={sentryRef}>
              <ActivityPlanItemsSkeleton />
            </div>
          )}
          {/*<ActivityPlanItemsSkeleton />*/}
        </LSActivityPlanItemsContainer>
      </LSActivityPlanTableContainer>
      <Dialog open={showPlanSendConfirmation} onClose={handleCloseAssignPlanDialog}>
        <LSPlanBeingSentModalDialogTitle>
          Do you want to suggest {planToSend?.title || 'this plan'} to {clientName}?{' '}
        </LSPlanBeingSentModalDialogTitle>
        <LSPlanBeingSentModalContent>
          {clientName} will receive a prompt to accept this plan.
        </LSPlanBeingSentModalContent>
        <DialogActions>
          <LSPlanOfferCancelButton onClick={handleCloseAssignPlanDialog}>
            Cancel
          </LSPlanOfferCancelButton>
          <AssignButton disabled={sending} onClick={suggestPlanToUser}>
            {sending && (
              <div
                style={{
                  position: 'absolute',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CircularProgress size={24} />
              </div>
            )}
            <span>Suggest</span>
          </AssignButton>
        </DialogActions>
      </Dialog>
    </div>
  )
}

function ActivityPlanItemsSkeleton() {
  const tableSkeletonSx = {
    borderBottom: '1px solid #0000003b',
  }
  const tableSkeletonProps = {
    width: '100%',
    height: '70px',
    animation: 'wave',
    variant: 'rectangular',
  }

  function ActivityPlanRowBareBones({ idx }) {
    return (
      <LSActivityPlanTableRows>
        <LSActivityPlanCells index={idx}>
          <Skeleton variant="text" sx={{ fontSize: '1rem', width: '100%' }} />
        </LSActivityPlanCells>
        <LSActivityPlanCells index={idx}>
          <Skeleton variant="text" sx={{ fontSize: '1rem', width: '100%' }} />
        </LSActivityPlanCells>
        <LSActivityPlanCells index={idx}>
          <Skeleton variant="text" sx={{ fontSize: '1rem', width: '100%' }} />
        </LSActivityPlanCells>
        <LSActivityPlanCells index={idx}>
          <Skeleton variant="text" sx={{ fontSize: '1rem', width: '100%' }} />
        </LSActivityPlanCells>
        <LSActivityPlanCells actionButton index={idx}>
          <Skeleton variant="rounded" width={80} height={25} />
        </LSActivityPlanCells>
      </LSActivityPlanTableRows>
    )
  }

  return (
    <>
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n, idx) => (
        <ActivityPlanRowBareBones key={idx} idx={idx} />
      ))}
    </>
  )
}

export function PlanBeingSentModal({
  planData,
  closeModal,
  userProfileData,
  togglePrewall,
  mentorName,
  setShowPlanDetailsModal,
}) {
  const [activityPlan, setActivityPlan] = useState(null)
  const [loading, setLoading] = useState(false)
  const [startingPlan, setStartingPlan] = useState()

  async function getActivityPlan() {
    if (planData?.id) {
      const response = await getMPDataWithParams('/external/feature', {
        feature: EXTERNAL_FEATURE_TYPES.activityPlan,
        id: planData?.id,
      })
      if (response.status === 200) {
        setActivityPlan(response?.data?.data?.['activity-plan'])
      }
    } else {
      toast.error('Something went wrong while receiving the activity plan.')
    }
  }

  function afterStartPlanFunction() {
    toast.success('Plan started successfully.')
    closeModal()
  }

  async function handleClickAccept() {
    setStartingPlan(true)
    await handleAccept(activityPlan, userProfileData, togglePrewall, afterStartPlanFunction)
    // let activityPlanSubscriptionPlanResponse
    // if (!!activityPlan?.pricePlan) {
    //   activityPlanSubscriptionPlanResponse = await getMPDataWithParams('/api/subscription-plan', {
    //     id: activityPlan?.pricePlan,
    //   })
    // }
    // if (userSubPlanIsFree) {
    //   if (
    //     activityPlanSubscriptionPlanResponse?.data?.subscriptionType?.id ===
    //     'subscription_normal' ||
    //     activityPlanSubscriptionPlanResponse?.data?.subscriptionType?.id === 'subscription_premium'
    //   ) {
    //     togglePrewall(activityPlanSubscriptionPlanResponse?.data)
    //   }
    // } else {
    //   if (userProfileData?.subscriptionPlanType === 'subscription_normal') {
    //     if (
    //       activityPlanSubscriptionPlanResponse?.data?.subscriptionType?.id ===
    //       'subscription_premium'
    //     ) {
    //       togglePrewall(activityPlanSubscriptionPlanResponse?.data)
    //     }
    //   }
    // }
    // await handleStartPlan()
    setStartingPlan(false)
  }

  async function Setup() {
    setLoading(true)
    await getActivityPlan()
    setLoading(false)
  }

  useEffect(() => {
    if (planData) {
      Setup()
    }
  }, [planData])

  return (
    <>
      <LSPlanBeingSentModalDialogTitle>{planData?.notifTitle}</LSPlanBeingSentModalDialogTitle>
      <LSPlanBeingSentModalContent>
        <LSPlanBeingSentModalTitle>
          <span>
            <strong>Plan Name: </strong>
            {activityPlan?.title}
          </span>
        </LSPlanBeingSentModalTitle>
        {activityPlan?.description && (
          <LSPlanBeingSentModalDescription>
            {activityPlan?.description}
          </LSPlanBeingSentModalDescription>
        )}
        {activityPlan?.totalDays !== undefined && (
          <div className="d-flex justify-content-start align-items-center gap-2">
            <strong>Total Days: </strong>
            <LSPlanBeingSentModalTotalDays>{activityPlan?.totalDays}</LSPlanBeingSentModalTotalDays>
          </div>
        )}
      </LSPlanBeingSentModalContent>
      <DialogActions>
        <LSPlanOfferCancelButton onClick={closeModal}>Dismiss</LSPlanOfferCancelButton>
        <Box sx={{ m: 1, position: 'relative' }}>
          <LSPlanOfferAcceptButton onClick={handleClickAccept}>
            Accept
            {startingPlan && (
              <StartingCircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </LSPlanOfferAcceptButton>
        </Box>
      </DialogActions>
    </>
  )
}

export const LSPastPrescriptionsComponent = ({ patientId }) => {
  const [pastPrescriptionsLoading, setPastPrescriptionsLoading] = useState(false)
  const [pastPrescriptionsData, setPastPrescriptionsData] = useState(null)
  const [emrSectionsData, setEmrSectionsData] = useState(null)

  async function setupThings() {
    setPastPrescriptionsLoading(true)
    const response = await getMPDataWithoutParams(
      `api/secure/patient/reports/list?patientId=${patientId}`,
    )
    setPastPrescriptionsLoading(false)
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      const prescriptionDataList = response?.data?.data?.prescriptionDataList || []
      sortArrayMomentBased(prescriptionDataList, 'createdTime', false)
      const resultArray = prescriptionDataList?.map(
        (pastPrecriptionsDataOnly, pastPrecriptionsDataOnlyIndex) => ({
          ...(response?.data?.data?.prescriptionTemplateList?.[0] || {}),
          prescriptionData: pastPrecriptionsDataOnly,
        }),
      )
      setPastPrescriptionsData(resultArray)
    }
  }

  useEffect(() => {
    setupThings()
  }, [])

  return (
    <div style={{ width: '100%' }}>
      {pastPrescriptionsLoading ? (
        <div style={{ padding: '10px' }}>
          <Spinner animation="border" variant="dark" size="md" />
        </div>
      ) : pastPrescriptionsData ? (
        pastPrescriptionsData?.map((pastPrescription, pastPrescriptionIndex) => {
          return (
            <div key={pastPrescriptionIndex} style={{ margin: '20px auto' }}>
              {/* <ViewOnlyPrescriptionVarTwo prescriptionData={pastPrescription} emrSections={emrSectionsData} /> */}
            </div>
          )
        })
      ) : (
        <div>No Data Available</div>
      )}
    </div>
  )
}

export const LSPastVisitReportsComponent = ({
  patientId,
  referenceBookedEventId,
  screeningSections,
  setIsPrintingPastHistoryReports,
  allVitalsOfThisPatient,
  mentorPrivateNotes,
  addTopMargins = false,
  prescriptionViewSettings,
  showOnlyTherapies = false,
  onlyPrescription = false,
  skeletonNumber = 5,
  hidePrintButton = false,
  customLoader,
  typeOfReportsToShowArray = ['prescription', 'therapy', 'screening'],
  patientHistoryGetApiSignature,
}) => {
  // const [loading, setLoading] = useState(false)
  const [dateMap, setDateMap] = useState({})

  const dispatch = useDispatch()

  const pastReportStoreData = useSelector(selectEmrPastReports)
  const pastReportData = pastReportStoreData?.data
  const loading = pastReportStoreData?.status === REDUX_STATUS_LOADING

  function setDatewiseMapData(incomingData) {
    // const prescriptions = incomingData?.['prescription']
    // const therapyReports = incomingData?.['therapy-report']
    // const screeningReports = incomingData?.['screening-report']
    const metaPrescriptions = incomingData?.['meta-prescription'] || []
    const therapyReportResponse =
      incomingData?.['therapy-report-response']?.map((response) => {
        return { ...response, type: 'therapy-report-response' }
      }) || []
    const screeningReportResponse =
      incomingData?.['screening-report-response']?.map((response) => {
        return { ...response, type: 'screening-report-response' }
      }) || []

    let allReports = [
      // ...prescriptions,
      // ...therapyReports,
      // ...screeningReports,
      // ...metaPrescriptions,
      // ...therapyReportResponse,
      // ...screeningReportResponse,
    ]

    if (typeOfReportsToShowArray.length > 0) {
      if (typeOfReportsToShowArray.includes('prescription')) {
        allReports = [...allReports, ...metaPrescriptions]
      }
      if (typeOfReportsToShowArray.includes('therapy')) {
        allReports = [...allReports, ...therapyReportResponse]
      }
      if (typeOfReportsToShowArray.includes('screening')) {
        allReports = [...allReports, ...screeningReportResponse]
      }
    }

    if (showOnlyTherapies) {
      allReports = [
        // ...prescriptions,
        // ...therapyReports,
        // ...screeningReports,
        ...therapyReportResponse,
      ]
    }
    if (onlyPrescription) {
      allReports = [
        ...metaPrescriptions,
        // ...therapyReports,
        // ...screeningReports,
        // ...therapyReportResponse,
      ]
    }

    const dateMapLocal = {}
    allReports.forEach((item) => {
      let timestamp = item.createdTime || item.modifiedTime || dayjs().toISOString()
      if (item?.apiParam === 'meta-prescription') {
        timestamp =
          item?.prescriptionData?.startTime ||
          item?.prescriptionData?.createdTime ||
          item?.prescriptionData?.modifiedTime ||
          dayjs().toISOString()
      }
      if (item?.type === 'therapy-report-response') {
        timestamp =
          item?.therapyReport?.createdTime ||
          item?.therapyReport?.modifiedTime ||
          dayjs().toISOString()
      }
      if (item?.type === 'screening-report-response') {
        timestamp =
          item?.screeningReport?.createdTime ||
          item?.screeningReport?.modifiedTime ||
          dayjs().toISOString()
      }

      const localDate = dayjs.utc(timestamp).local().format('DD/MM/YYYY, dddd')
      if (!dateMapLocal[localDate]) {
        dateMapLocal[localDate] = []
      }
      dateMapLocal[localDate].push(item)
    })

    setDateMap(dateMapLocal)
  }

  // async function setupThings() {
  //   setLoading(true)

  //   let uri = `/api/secure/patient/reports/list?patientId=${patientId}`
  //   if (referenceBookedEventId) {
  //     uri += `&referenceBookedEventId=${referenceBookedEventId}`
  //   }
  //   const response = await getMPDataWithoutParams(uri)
  //   setLoading(false)
  //   if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
  //     setDatewiseMapData(response.data.data)
  //   } else {
  //     toast.error('Error getting patient reports')
  //   }
  // }

  useEffect(() => {
    if (pastReportData) {
      setDatewiseMapData(pastReportData)
    }
  }, [pastReportData])

  useEffect(() => {
    // if (pastReportStoreData?.status !== REDUX_STATUS_IDLE) {
    //   return
    // }
    dispatch(
      getPastPatientReports({
        patientId: patientId,
        referenceBookedEventId: referenceBookedEventId,
        patientHistoryGetApiSignature: patientHistoryGetApiSignature,
      }),
    )
  }, [])

  

  return (
    <>
      {' '}
      {loading ? (
        <>
          {customLoader
            ? customLoader
            : Array.from({ length: skeletonNumber }, () => 1).map(() => (
                <>
                  <LSPastReportsSkeleton
                    variant="rectangular"
                    animation="wave"
                    width="100%"
                    height={200}
                    sx={{
                      '&.MuiSkeleton-wave::after': {
                        background:
                          'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.2), transparent)', // Darker wave
                        opacity: 0.8, // Increase visibility
                      },
                    }}
                  />
                  <div style={{ height: '24px' }}></div>
                </>
              ))}
        </>
      ) : (
        <div
          className="d-flex flex-column w-100"
          style={{ marginTop: addTopMargins ? '20px' : '0px' }}
        >
          {Object.keys(dateMap || {}).length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <div className="text-muted text-center">
                <h5 className="mb-2">No Past Reports Available</h5>
                <p className="mb-0">There are no past visit reports to display for this patient.</p>
              </div>
            </div>
          ) : (
            Object.keys(dateMap || {})
              ?.sort((a, b) => {
                const parseDate = (dateStr) => dayjs(dateStr.split(',')[0], 'DD/MM/YYYY')
                return parseDate(b).valueOf() - parseDate(a).valueOf() // Sort by latest first
              })
              ?.map((date, index) => (
                <React.Fragment key={date}>
                  <VisitReportsComponent
                    visitNo={Object.keys(dateMap).length - index}
                    date={date}
                    allVitalsOfThisPatient={allVitalsOfThisPatient}
                    reports={dateMap?.[date]}
                    screeningSections={screeningSections}
                    setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
                    mentorPrivateNotes={mentorPrivateNotes}
                    prescriptionViewSettings={prescriptionViewSettings}
                    hidePrintButton={hidePrintButton}
                  />
                </React.Fragment>
              ))
          )}
        </div>
      )}
    </>
  )
}

export const AudioInputToggleComponent = ({ isActive, clickHandler, error, iconSize = '28px' }) => {
  return (
    <MicCameraToggleContainer isActive={isActive} onClick={clickHandler} iconSize={iconSize}>
      {isActive ? (
        <KeyboardVoiceOutlinedIcon 
          style={{ 
            fontSize: iconSize,
            color: '#ffffff',
            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))',
            strokeWidth: 1.5,
          }} 
        />
      ) : (
        <MicOffOutlinedIcon 
          style={{ 
            fontSize: iconSize,
            color: '#ffffff',
            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))',
            strokeWidth: 1.5,
          }} 
        />
      )}
      {error && <LiveSessionErrorSign>!</LiveSessionErrorSign>}
    </MicCameraToggleContainer>
  )
}

export const VideoInputToggleComponent = ({ isActive, clickHandler, error, iconSize = '28px' }) => {
  return (
    <MicCameraToggleContainer isActive={isActive} onClick={clickHandler} iconSize={iconSize}>
      {isActive ? (
        <VideocamOutlinedIcon 
          style={{ 
            fontSize: iconSize,
            color: '#ffffff',
            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))',
            strokeWidth: 1.5,
          }} 
        />
      ) : (
        <VideocamOffOutlinedIcon 
          style={{ 
            fontSize: iconSize,
            color: '#ffffff',
            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))',
            strokeWidth: 1.5,
          }} 
        />
      )}
      {error && <LiveSessionErrorSign>!</LiveSessionErrorSign>}
    </MicCameraToggleContainer>
  )
}

export const AddToGoogleCalendarButton = ({
  name,
  description,
  utcStartDateTime,
  utcEndDateTime,
}) => {
  const handleButtonClick = () => {
    const googleCalendarLink = addToGoogleCalendarLink(
      utcStartDateTime,
      utcEndDateTime,
      name,
      description,
      moment,
    )
    // &sf=true&output=xml

    // Open the link in a new tab
    window.open(googleCalendarLink, '_blank')
  }

  return (
    <AddtoCalendarStyledButton onClick={handleButtonClick}>
      Add to Calendar{' '}
      <img style={{ width: '23px' }} src={googleCalendarIcon} alt="google_calendar_icon" />
    </AddtoCalendarStyledButton>
  )
}

export const AskBothCameraAndMicrophonePermissionsDialog = ({
  open,
  onCloseFunction,
  setMicrophoneError,
  setCameraError,
  setAvailableMicrophones,
  setSelectedMicrophone,
  setAvailableSpeakers,
  setSelectedSpeaker,
  setAvailableCameras,
  setSelectedCamera,
  sessionType = 'consultation',
}) => {
  const [permissionState, setPermissionState] = useState('initial') // initial, requesting, blocked
  
  // Detect platform for better UX
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent)

  // Check if permissions are blocked
  useEffect(() => {
    if (open) {
      checkPermissionStatus()
    } else {
      setPermissionState('initial')
    }
  }, [open])

  const checkPermissionStatus = async () => {
    try {
      if (navigator.permissions) {
        const [micPermission, camPermission] = await Promise.all([
          navigator.permissions.query({ name: 'microphone' }).catch(() => ({ state: 'prompt' })),
          navigator.permissions.query({ name: 'camera' }).catch(() => ({ state: 'prompt' })),
        ])
        
        if (micPermission.state === 'denied' || camPermission.state === 'denied') {
          setPermissionState('blocked')
          return
        }
        
        if (micPermission.state === 'granted' && camPermission.state === 'granted') {
          // Already have permissions
          handlePermissionsGranted()
          return
        }
      }
      setPermissionState('initial')
    } catch (error) {
      setPermissionState('initial')
    }
  }

  const handlePermissionsGranted = async () => {
    try {
      setMicrophoneError(false)
      setCameraError(false)
      // Use Promise.allSettled to continue even if some device enumeration fails
      // This is critical on iOS Safari where device enumeration may fail or return empty arrays
      await Promise.allSettled([
        getAndSetMicrophones({ setAvailableMicrophones, setSelectedMicrophone }),
        getAndSetCameras({ setAvailableCameras, setSelectedCamera }),
        getAndSetSpeakers({ setAvailableSpeakers, setSelectedSpeaker }),
      ])
    } catch (error) {
      console.error('Error setting up devices:', error)
    } finally {
      // Always close the dialog, even if device enumeration fails
      // This prevents the "Waiting for your permission" stuck state on iOS
      onCloseFunction()
    }
  }

  const requestPermissions = async () => {
    setPermissionState('requesting')
    
    try {
      // Increased timeout for iOS Safari which can be slow to activate camera
      const timeoutDuration = isIOS ? 30000 : 15000
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('TIMEOUT')), timeoutDuration)
      })
      
      const mediaPromise = navigator.mediaDevices.getUserMedia({ 
        audio: true, 
        video: true 
      })
      
      const stream = await Promise.race([mediaPromise, timeoutPromise])
      stream.getTracks().forEach(track => track.stop())
      
      // On iOS Safari, add a small delay before device enumeration
      // This gives the browser time to properly register the permissions
      if (isIOS) {
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      handlePermissionsGranted()
    } catch (error) {
      console.error('Permission request failed:', error)
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setPermissionState('blocked')
        setMicrophoneError(true)
        setCameraError(true)
      } else if (error.message === 'TIMEOUT') {
        // On timeout, show blocked state with recovery instructions
        setPermissionState('blocked')
        setMicrophoneError(true)
        setCameraError(true)
      } else {
        // Generic error - on iOS Safari, try to proceed anyway
        // as the error might be a false negative
        if (isIOS) {
          console.warn('iOS Safari permission error - attempting to proceed:', error)
          handlePermissionsGranted()
        } else {
          setPermissionState('blocked')
          setMicrophoneError(true)
          setCameraError(true)
        }
      }
    }
  }

  const handleSkip = () => {
    setMicrophoneError(true)
    setCameraError(true)
    onCloseFunction()
  }

  // Get recovery instructions based on platform
  const getRecoveryInstructions = () => {
    if (isIOS) {
      return [
        'Open Settings app on your device',
        'Scroll down and tap Safari (or your browser)',
        'Tap "Camera" → Allow',
        'Tap "Microphone" → Allow',
        'Come back and tap "Try Again"',
      ]
    }
    if (isAndroid) {
      return [
        'Tap the lock icon (🔒) in address bar',
        'Tap "Permissions" or "Site settings"',
        'Turn on Camera and Microphone',
        'Come back and tap "Try Again"',
      ]
    }
    return [
      'Look for a camera icon in your address bar',
      'Click it and allow camera & microphone',
      'Then tap "Try Again" below',
    ]
  }

  return (
    <Dialog
      key="both permission dialog"
      open={open}
      onClose={permissionState !== 'requesting' ? onCloseFunction : undefined}
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: isMobile ? '100%' : '480px',
            borderRadius: isMobile ? '24px 24px 0 0' : '24px',
            position: isMobile ? 'absolute' : 'relative',
            bottom: isMobile ? 0 : 'auto',
            margin: 0,
          },
        },
      }}
    >
      <Box sx={{ p: 3, textAlign: 'center' }}>
        {/* Close button */}
        {permissionState !== 'requesting' && (
          <CloseIcon 
            onClick={onCloseFunction} 
            style={{ 
              position: 'absolute', 
              top: '16px', 
              right: '16px', 
              cursor: 'pointer',
              color: '#9ca3af',
            }} 
          />
        )}

        {/* Initial State - Ask for permission */}
        {permissionState === 'initial' && (
          <>
            <Box
              sx={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 4px 16px rgba(99, 102, 241, 0.2)',
              }}
            >
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="20" r="10" fill="#6366f1" opacity="0.9"/>
                <ellipse cx="28" cy="42" rx="16" ry="10" fill="#6366f1" opacity="0.9"/>
                <circle cx="44" cy="40" r="8" fill="#10b981"/>
                <rect x="42" y="36" width="4" height="6" rx="2" fill="white"/>
                <path d="M40 42 C40 44 42 46 44 46 C46 46 48 44 48 42" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                <line x1="44" y1="46" x2="44" y2="48" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </Box>

            <Box component="h2" sx={{ fontSize: '22px', fontWeight: 700, color: '#111827', m: 0, mb: 1.5 }}>
              Your doctor needs to see and hear you
            </Box>
            
            <Box sx={{ fontSize: '15px', color: '#6b7280', mb: 3, lineHeight: 1.6 }}>
              To have a proper {sessionType}, please allow camera and microphone access. 
              This helps your healthcare provider understand you better.
            </Box>

            {/* Trust indicators */}
            <Box sx={{ background: '#f9fafb', borderRadius: '12px', p: 2, mb: 3, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <span>🔒</span>
                <span style={{ fontSize: '14px', color: '#4b5563' }}>Your video is private and secure</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <span>⏹️</span>
                <span style={{ fontSize: '14px', color: '#4b5563' }}>Turn off camera anytime during call</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <span>🔇</span>
                <span style={{ fontSize: '14px', color: '#4b5563' }}>Mute yourself whenever you want</span>
              </Box>
            </Box>

            <Box
              component="button"
              onClick={requestPermissions}
              sx={{
                width: '100%',
                padding: '16px',
                fontSize: '17px',
                fontWeight: 600,
                color: '#ffffff',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                borderRadius: '14px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)',
                mb: 2,
              }}
            >
              ✓ Allow Camera & Microphone
            </Box>

            <Box
              component="button"
              onClick={handleSkip}
              sx={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                color: '#9ca3af',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Join without camera/microphone
            </Box>
            <Box sx={{ fontSize: '12px', color: '#d1d5db', mt: 1 }}>
              (Not recommended - your doctor won't see or hear you)
            </Box>
          </>
        )}

        {/* Requesting State - Waiting for browser prompt */}
        {permissionState === 'requesting' && (
          <>
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.05)', opacity: 0.8 },
                },
              }}
            >
              <span style={{ fontSize: '36px' }}>👆</span>
            </Box>

            <Box component="h3" sx={{ fontSize: '20px', fontWeight: 600, color: '#111827', m: 0, mb: 1.5 }}>
              Tap "Allow" when prompted
            </Box>

            <Box sx={{ fontSize: '15px', color: '#6b7280', lineHeight: 1.6 }}>
              A popup will appear at the top of your screen.
              <br />
              Please tap <strong>"Allow"</strong> to continue.
            </Box>

            <Box sx={{ mt: 4, p: 2, background: '#f3f4f6', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#fbbf24',
                  animation: 'blink 1s ease-in-out infinite',
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }}
              />
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Waiting for your permission...</span>
            </Box>
          </>
        )}

        {/* Blocked State - Show recovery instructions */}
        {permissionState === 'blocked' && (
          <>
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <span style={{ fontSize: '36px' }}>⚙️</span>
            </Box>

            <Box component="h2" sx={{ fontSize: '20px', fontWeight: 700, color: '#111827', m: 0, mb: 1 }}>
              Camera & Microphone Blocked
            </Box>

            <Box sx={{ fontSize: '14px', color: '#ef4444', mb: 3, fontWeight: 500 }}>
              Your doctor won't be able to see or hear you
            </Box>

            <Box sx={{ background: '#f9fafb', borderRadius: '16px', p: 2.5, mb: 3, textAlign: 'left' }}>
              <Box sx={{ fontSize: '16px', fontWeight: 600, color: '#111827', mb: 2 }}>
                {isIOS ? 'How to allow on iPhone/iPad' : isAndroid ? 'How to allow on Android' : 'How to allow permissions'}
              </Box>
              
              <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                {getRecoveryInstructions().map((step, index) => (
                  <Box
                    component="li"
                    key={index}
                    sx={{
                      fontSize: '14px',
                      color: '#4b5563',
                      mb: 1.5,
                      lineHeight: 1.5,
                      '&::marker': { color: '#6366f1', fontWeight: 600 },
                    }}
                  >
                    {step}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              component="button"
              onClick={requestPermissions}
              sx={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#ffffff',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                border: 'none',
                borderRadius: '14px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                mb: 2,
              }}
            >
              🔄 I've allowed it - Try Again
            </Box>

            <Box
              component="button"
              onClick={handleSkip}
              sx={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#9ca3af',
                background: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                cursor: 'pointer',
              }}
            >
              Continue without camera/microphone
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  )
}

export const AskSinglePermissionDialog = ({
  type,
  open,
  onCloseFunction,
  setMicrophoneError,
  setCameraError,
  setAvailableMicrophones,
  setSelectedMicrophone,
  setAvailableSpeakers,
  setSelectedSpeaker,
  setAvailableCameras,
  setSelectedCamera,
}) => {
  const [permissionState, setPermissionState] = useState('initial') // initial, requesting, blocked
  
  // Detect platform
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent)

  const isMicrophone = type === 'microphone'
  const permissionName = isMicrophone ? 'microphone' : 'camera'
  const permissionIcon = isMicrophone ? '🎤' : '📷'

  useEffect(() => {
    if (open) {
      checkAndRequestPermission()
    } else {
      setPermissionState('initial')
    }
  }, [open, type])

  const checkAndRequestPermission = async () => {
    try {
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: permissionName }).catch(() => ({ state: 'prompt' }))
        
        if (permission.state === 'granted') {
          handlePermissionGranted()
          return
        }
        
        if (permission.state === 'denied') {
          setPermissionState('blocked')
          return
        }
      }
      
      // Auto-request permission
      setPermissionState('requesting')
      requestPermission()
    } catch (error) {
      setPermissionState('initial')
    }
  }

  const handlePermissionGranted = async () => {
    try {
      if (isMicrophone) {
        setMicrophoneError(false)
        // Use Promise.allSettled to continue even if device enumeration fails
        await Promise.allSettled([
          getAndSetMicrophones({ setAvailableMicrophones, setSelectedMicrophone }),
          getAndSetSpeakers({ setAvailableSpeakers, setSelectedSpeaker }),
        ])
      } else {
        setCameraError(false)
        await getAndSetCameras({ setAvailableCameras, setSelectedCamera }).catch(() => {})
      }
    } catch (error) {
      console.error('Error setting up devices:', error)
    } finally {
      // Always close the dialog, even if device enumeration fails
      // This prevents the "Waiting for permission" stuck state on iOS
      onCloseFunction()
    }
  }

  const requestPermission = async () => {
    try {
      // Increased timeout for iOS Safari which can be slow to activate camera
      const timeoutDuration = isIOS ? 30000 : 15000
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('TIMEOUT')), timeoutDuration)
      })
      
      const constraints = isMicrophone ? { audio: true } : { video: true }
      const mediaPromise = navigator.mediaDevices.getUserMedia(constraints)
      
      const stream = await Promise.race([mediaPromise, timeoutPromise])
      stream.getTracks().forEach(track => track.stop())
      
      // On iOS Safari, add a small delay before device enumeration
      if (isIOS) {
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      handlePermissionGranted()
    } catch (error) {
      console.error('Permission request failed:', error)
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setPermissionState('blocked')
        if (isMicrophone) {
          setMicrophoneError(true)
        } else {
          setCameraError(true)
        }
      } else if (error.message === 'TIMEOUT') {
        setPermissionState('blocked')
        if (isMicrophone) {
          setMicrophoneError(true)
        } else {
          setCameraError(true)
        }
      } else {
        // Generic error - on iOS Safari, try to proceed anyway
        if (isIOS) {
          console.warn('iOS Safari permission error - attempting to proceed:', error)
          handlePermissionGranted()
        } else {
          setPermissionState('blocked')
          if (isMicrophone) {
            setMicrophoneError(true)
          } else {
            setCameraError(true)
          }
        }
      }
    }
  }

  const handleRetry = () => {
    setPermissionState('requesting')
    requestPermission()
  }

  const handleSkip = () => {
    if (isMicrophone) {
      setMicrophoneError(true)
    } else {
      setCameraError(true)
    }
    onCloseFunction()
  }

  // Platform-specific recovery instructions
  const getRecoveryInstructions = () => {
    const deviceName = isMicrophone ? 'Microphone' : 'Camera'
    if (isIOS) {
      return [
        'Open Settings app',
        `Tap Safari → ${deviceName} → Allow`,
        'Come back and tap "Try Again"',
      ]
    }
    if (isAndroid) {
      return [
        'Tap the lock icon (🔒) in address bar',
        `Turn on ${deviceName}`,
        'Tap "Try Again"',
      ]
    }
    return [
      `Click the ${isMicrophone ? 'microphone' : 'camera'} icon in address bar`,
      `Select "Allow" for ${permissionName}`,
      'Click "Try Again"',
    ]
  }

  return (
    <Dialog
      key="ask single permission"
      open={open}
      onClose={permissionState !== 'requesting' ? onCloseFunction : undefined}
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: isMobile ? '100%' : '420px',
            borderRadius: isMobile ? '24px 24px 0 0' : '24px',
            position: isMobile ? 'absolute' : 'relative',
            bottom: isMobile ? 0 : 'auto',
            margin: 0,
          },
        },
      }}
    >
      <Box sx={{ p: 3, textAlign: 'center' }}>
        {permissionState !== 'requesting' && (
          <CloseIcon 
            onClick={onCloseFunction} 
            style={{ 
              position: 'absolute', 
              top: '16px', 
              right: '16px', 
              cursor: 'pointer',
              color: '#9ca3af',
            }} 
          />
        )}

        {/* Requesting State */}
        {permissionState === 'requesting' && (
          <>
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.05)', opacity: 0.8 },
                },
              }}
            >
              <span style={{ fontSize: '36px' }}>👆</span>
            </Box>

            <Box component="h3" sx={{ fontSize: '20px', fontWeight: 600, color: '#111827', m: 0, mb: 1.5 }}>
              Tap "Allow" when prompted
            </Box>

            <Box sx={{ fontSize: '15px', color: '#6b7280', lineHeight: 1.6, mb: 3 }}>
              A popup will appear asking for {permissionName} access.
              <br />
              Please tap <strong>"Allow"</strong> to continue.
            </Box>

            <Box sx={{ p: 2, background: '#f3f4f6', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#fbbf24',
                  animation: 'blink 1s ease-in-out infinite',
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }}
              />
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Waiting for permission...</span>
            </Box>
          </>
        )}

        {/* Blocked State */}
        {permissionState === 'blocked' && (
          <>
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <span style={{ fontSize: '36px' }}>{permissionIcon}</span>
            </Box>

            <Box component="h2" sx={{ fontSize: '20px', fontWeight: 700, color: '#111827', m: 0, mb: 1 }}>
              {isMicrophone ? 'Microphone' : 'Camera'} Blocked
            </Box>

            <Box sx={{ fontSize: '14px', color: '#ef4444', mb: 3, fontWeight: 500 }}>
              {isMicrophone ? "Your doctor won't be able to hear you" : "Your doctor won't be able to see you"}
            </Box>

            <Box sx={{ background: '#f9fafb', borderRadius: '16px', p: 2.5, mb: 3, textAlign: 'left' }}>
              <Box sx={{ fontSize: '15px', fontWeight: 600, color: '#111827', mb: 2 }}>
                How to allow {permissionName}:
              </Box>
              
              <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                {getRecoveryInstructions().map((step, index) => (
                  <Box
                    component="li"
                    key={index}
                    sx={{
                      fontSize: '14px',
                      color: '#4b5563',
                      mb: 1.5,
                      lineHeight: 1.5,
                      '&::marker': { color: '#6366f1', fontWeight: 600 },
                    }}
                  >
                    {step}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              component="button"
              onClick={handleRetry}
              sx={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#ffffff',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                border: 'none',
                borderRadius: '14px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                mb: 2,
              }}
            >
              🔄 I've allowed it - Try Again
            </Box>

            <Box
              component="button"
              onClick={handleSkip}
              sx={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#9ca3af',
                background: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                cursor: 'pointer',
              }}
            >
              Continue without {permissionName}
            </Box>
          </>
        )}

        {/* Initial State (fallback - usually auto-transitions to requesting) */}
        {permissionState === 'initial' && (
          <>
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <span style={{ fontSize: '36px' }}>{permissionIcon}</span>
            </Box>

            <Box component="h2" sx={{ fontSize: '20px', fontWeight: 700, color: '#111827', m: 0, mb: 1.5 }}>
              {isMicrophone ? 'Allow Microphone Access' : 'Allow Camera Access'}
            </Box>

            <Box sx={{ fontSize: '15px', color: '#6b7280', mb: 3, lineHeight: 1.6 }}>
              {isMicrophone 
                ? 'Your doctor needs to hear you during the session. You can mute yourself anytime.'
                : 'Your doctor needs to see you during the session. You can turn off video anytime.'}
            </Box>

            <Box
              component="button"
              onClick={handleRetry}
              sx={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#ffffff',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                borderRadius: '14px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)',
                mb: 2,
              }}
            >
              ✓ Allow {isMicrophone ? 'Microphone' : 'Camera'}
            </Box>

            <Box
              component="button"
              onClick={handleSkip}
              sx={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                color: '#9ca3af',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Skip for now
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  )
}

export const SelectForMediaDevices = ({ value, handleChange, options }) => {
  return (
    <FormControl sx={{ m: 1, width: 300 }}>
      <Select
        value={value}
        onChange={handleChange}
        displayEmpty
        inputProps={{ 'aria-label': 'Without label' }}
      >
        {options?.map((option) => (
          <MenuItem key={option?.deviceId} value={option?.deviceId}>
            {option?.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export const LSInvestigationsPageComponent = (props) => {
  const { patientId, clinicBrandId, mentorId, name } = props
  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        // padding: '80px 16px',
        alignItems: 'center',
        flexDirection: 'column',
        gap: '24px',
        background: '#f0f0f0',
      }}
    >
      <div
        style={{
          border: '1px solid #d3d3d3',
          width: '100%',
          background: '#fff',
        }}
      >
        <AddOrViewInvestigationsForSelectedPerson heightLimit={'calc(100vh - 140px)'} {...props} />
      </div>
    </div>
  )
}

// if (report?.screeningReportId) {
//   return (
//     <PrintableScreeningReportComponent
//       key={idx}
//       report={report}
//       headingText={`Screened by ${report?.mentorName} (${dayjs(
//         report?.createdTime || report?.modifiedTime,
//       ).format(MOMENT_FORMATS.onlyTimeDetails)})`}
//       setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
//     />
//   )

/* if (report?.therapyReportId) {
  return (
    <TherapyReportPreview
      key={idx}
      report={report}
      headingText={`Therapy by ${report?.mentorName} (${dayjs(
        report?.createdTime || report?.modifiedTime,
      ).format(MOMENT_FORMATS.onlyTimeDetails)})`}
      setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
    />
  )
} */

// export const TherapyReportViewComponentForDoctor = ({
//   propPatientName,
//   propPatientGender,
//   propPatientDateOfBirth,
// }) => {
//   const therapyReportData = {
//     createdTime: '2024-04-22 07:34:55',
//     modifiedTime: '2024-04-22 07:34:55',
//     patientName: propPatientName || 'Ponpon',
//     patientGender: propPatientGender || 'nonBinary',
//     patientDateOfBirth: propPatientDateOfBirth || '2011-04-02 00:00:00',
//   }
//   const observationAndAdvice = {
//     advice: 'Engage in activities you enjoy and reach out for support from friends, family, or a trusted adult when feeling overwhelmed.',
//     observation: 'Exhibited signs of anxiety, including restlessness and difficulty concentrating.',
//   }
//   const selectedDiagnosis = [{ label: 'DEPRESSION', value: 'DEPRESSION' }]
//   const selectedComplaints = [{ label: 'DECREASED SLEEP', value: 'DECREASED SLEEP' }]
//   const nextVisitInputType = 'days'
//   const nextVisitInputValue = 30

//   return (
//     <div style={{ width: '100%' }}>
//       <PMPParent>
//         <TherapyReportHeader therapyReportData={therapyReportData} therapistName={'Somya Singh'} />
//         <div style={{ flex: 1, padding: '20px' }}>
//           <TherapyReportBodyFirstSection
//             observationAndAdvice={observationAndAdvice}
//             selectedComplaints={selectedComplaints}
//             selectedDiagnosis={selectedDiagnosis}
//           />
//           <TherapyReportBody
//             observationAndAdvice={observationAndAdvice}
//             nextVisitInputType={nextVisitInputType}
//             nextVisitInputValue={nextVisitInputValue}
//           />
//         </div>
//         <TherapyReportFooter therapyReportData={therapyReportData} />
//       </PMPParent>
//     </div>
//   )
// }

// {/* <div
//   style={{
//     border: '1px solid #d3d3d3',
//     width: '100%',
//     background: '#fff',
//   }}
// >
//   <div style={{ padding: '16px', fontSize: '20px', fontWeight: '500', textAlign: 'center' }}>
//     Add Patient Investigation Results
//   </div>
//   <NewInvestigationsForSelectedPerson {...props} />
// </div>
// <div style={{ height: '1px', width: '100%', background: '#d3d3d3' }}></div>
// <div
//   style={{
//     border: '1px solid #d3d3d3',
//     width: '100%',
//     background: '#fff',
//   }}
// >
//   <div style={{ padding: '16px', fontSize: '20px', fontWeight: '500', textAlign: 'center' }}>
//     View Patient Investigation Results
//   </div>
//   <ViewInvestigationsForSelectedPerson {...props} />
// </div>
// <div style={{ height: '1px', width: '100%', background: '#d3d3d3' }}></div> */}

{
  /* <div key={idx}>
                <div>
                  <u>Therapy Report</u>
                </div>
                <PMPParent>
                  <TherapyReportHeader
                    therapyReportData={report}
                    therapistName={report?.mentorName}
                  />
                  <div style={{ flex: 1, padding: '20px' }}>
                    <TherapyReportBodyFirstSection
                      observationAndAdvice={{
                        advice: report?.advice || null,
                        observation: report?.observation || null,
                      }}
                      selectedComplaints={getInitialDataForSettingExistingSelectedComplaints(
                        report?.complaints,
                      )}
                      selectedDiagnosis={getInitialDataForSettingExistingSelectedDiagnosis(
                        report?.diagnosis,
                      )}
                    />
                    <TherapyReportBody
                      observationAndAdvice={{
                        advice: report?.advice || null,
                        observation: report?.observation || null,
                      }}
                      content={report?.content}
                      nextVisitInputType={'days'}
                      nextVisitInputValue={getInitialDataForSettingDaysOfNextVisit(
                        report?.nextVisitDate,
                        report?.createdTime,
                      )}
                    />
                  </div>
                  <TherapyReportFooter therapyReportData={report} />
                </PMPParent>
              </div> */
}

// <div key={idx}>
//   <div>
//     <u>Screening Report</u>
//   </div>
//   <PMPParent>
//     <ScreeningReportHeader
//       screeningReportData={report}
//       screenerName={report?.mentorName}
//     />
//     <div style={{ flex: 1, padding: '20px' }}>
//       <ScreeningReportBodyFirstSection
//         observationAndAdvice={{
//           advice: report?.advice || null,
//           observation: report?.observation || null,
//         }}
//         selectedComplaints={getSRInitialDataForSettingExistingSelectedComplaints(
//           report?.complaints,
//         )}
//         selectedDiagnosis={getSRInitialDataForSettingExistingSelectedDiagnosis(
//           report?.diagnosis,
//         )}
//         // screeningSections={screeningSections}
//         // screeningQuestionAnswers={report?.screeningAnswers}
//         reportData={report}
//       />
//       <ScreeningReportBody
//         observationAndAdvice={{
//           advice: report?.advice || null,
//           observation: report?.observation || null,
//         }}
//         content={report?.content}
//         suggestedBookingTherapy={report?.suggestedBookingTherapy}
//       />
//     </div>
//   </PMPParent>
// </div>

